import {
  type FixedDepartureListItem,
  type FixedDepartureListQuery,
  type FixedDepartureStatus,
} from '@utazzo/shared';
import { Copy, Pencil } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useDeleteFixedDeparture,
  useDuplicateFixedDeparture,
  useFixedDepartureFacets,
  useFixedDeparturesList,
} from '../use-fixed-departures';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { ReadinessCell } from '@/components/ReadinessCell';
import { SoftPill } from '@/components/soft-pill';
import { useCurrentUser } from '@/features/auth/use-auth';
import {
  useCatalogGeoFilters,
  useSelectedCountryId,
  withGeoFilters,
} from '@/hooks/use-catalog-geo-filters';

const STATUS_LABEL: Record<FixedDepartureStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending approval',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  ARCHIVED: 'Archived',
};

const STATUS_TONE: Record<FixedDepartureStatus, string> = {
  DRAFT: 'border border-zinc-200 bg-zinc-50 text-zinc-700',
  PENDING_APPROVAL: 'border border-amber-200 bg-amber-50 text-amber-700',
  ACTIVE: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
  INACTIVE: 'border border-zinc-200 bg-zinc-100 text-zinc-600',
  ARCHIVED: 'border border-rose-200 bg-rose-50 text-rose-700',
};

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

/**
 * The Readiness column, turned into two filters so "what still needs work?" is
 * one click instead of a page-by-page read of the chips. Both are server-side,
 * so the count and the pagination describe the filtered set, not the page.
 */
const IMAGES_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Any images' },
  { value: 'with', label: 'With images' },
  { value: 'without', label: 'Without images' },
];

const INFO_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Any information' },
  { value: 'missing', label: 'Missing information' },
  { value: 'complete', label: 'Complete information' },
];

const FILTERS: ReadonlyArray<DataGridFilter> = [
  { id: 'status', label: 'Status', options: STATUS_OPTIONS, widthClass: 'w-48' },
  { id: 'images', label: 'Images', options: IMAGES_OPTIONS, widthClass: 'w-44' },
  { id: 'info', label: 'Info', options: INFO_OPTIONS, widthClass: 'w-48' },
];

const MIN_SEARCH_LENGTH = 3;

export const FixedDeparturesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteFixedDeparture = useDeleteFixedDeparture();
  const duplicateFixedDeparture = useDuplicateFixedDeparture();
  const navigate = useNavigate();

  // Country / City, from the template facets (places templates actually visit).
  // City narrows to the selected Country; switching Country clears a stale City
  // - see `useDataGridState.setFilter`.
  const facets = useFixedDepartureFacets();
  const { countryFilter, cityFilter } = useCatalogGeoFilters(facets.data, useSelectedCountryId());
  const filters = useMemo(
    () => withGeoFilters(FILTERS, countryFilter, cityFilter, 'status'),
    [countryFilter, cityFilter],
  );

  const columns: ListScreenColumns<FixedDepartureListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<FixedDepartureListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'reference',
        header: 'Reference',
        sortable: true,
        width: '160px',
        cell: (row) => <span className="font-mono text-xs text-zinc-600">{row.reference}</span>,
      },
      {
        id: 'title',
        header: 'Fixed departure',
        sortable: true,
        cell: (row) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-zinc-900">{row.title}</div>
            {row.citySummary ? (
              <div className="truncate text-xs text-zinc-500">{row.citySummary}</div>
            ) : (
              <div className="text-xs text-zinc-400">No cities configured yet</div>
            )}
          </div>
        ),
      },
      {
        id: 'duration',
        header: 'Duration',
        width: '120px',
        cell: (row) =>
          row.numberOfNights > 0 ? (
            <span className="text-zinc-700">
              {row.numberOfNights}N / {row.numberOfDays}D
            </span>
          ) : (
            <span className="text-zinc-400">-</span>
          ),
      },
      {
        id: 'readiness',
        header: 'Readiness',
        width: '130px',
        // A template owns no images itself - `imageCount` totals the day heroes
        // and their galleries, which is what ends up on the docket / PDF.
        cell: (row) => (
          <ReadinessCell
            photoCount={row.imageCount}
            checks={[
              { label: 'Starting price', ok: row.startingPricePaise !== null },
              { label: 'Destinations', ok: Boolean(row.citySummary) },
              { label: 'Categories', ok: Boolean(row.categorySummary) },
            ]}
          />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        width: '140px',
        cell: (row) => (
          <SoftPill className={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</SoftPill>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<FixedDepartureListItem, FixedDepartureListQuery>
      title="Fixed Departures"
      description=""
      useList={useFixedDeparturesList}
      defaultSort={{ by: 'createdAt', dir: 'desc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const images = state.filters.images;
        const info = state.filters.info;
        const countryId = state.filters.countryId;
        const cityId = state.filters.cityId;
        return {
          page: base.page,
          pageSize: base.pageSize,
          sortBy: base.sortBy as FixedDepartureListQuery['sortBy'],
          sortDir: base.sortDir,
          ...(base.search ? { q: base.search } : {}),
          ...(status ? { status: status as FixedDepartureStatus } : {}),
          ...(countryId ? { countryId } : {}),
          ...(cityId ? { cityId } : {}),
          ...(images === 'with' ? { hasImages: true } : {}),
          ...(images === 'without' ? { hasImages: false } : {}),
          ...(info === 'missing' ? { missingInfo: true } : {}),
          ...(info === 'complete' ? { missingInfo: false } : {}),
        };
      }}
      columns={columns}
      filters={filters}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(`/fixed-departures/${row.id}`)}
      createButton={{
        label: 'Add fixed departure',
        onClick: () => navigate('/fixed-departures/new'),
      }}
      rowActions={{
        extra: (row) => [
          {
            type: 'item',
            label: 'Edit',
            icon: <Pencil className="h-4 w-4" />,
            onSelect: () => navigate(`/fixed-departures/${row.id}`),
          },
          {
            type: 'item',
            label: 'Duplicate to draft',
            icon: <Copy className="h-4 w-4" />,
            disabled: duplicateFixedDeparture.isPending,
            onSelect: () => void duplicateFixedDeparture.mutateAsync(row.id),
          },
        ],
        delete: {
          onDelete: (row) => deleteFixedDeparture.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this fixed departure?',
            description: `${row.title} (${row.reference}) will be soft-deleted. Itineraries already built from it are unaffected.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by title or reference (min 3 characters)',
        emptyTitle: 'No fixed departures match these filters',
        emptyDescription:
          'Adjust the filters or clear the search box, or create your first fixed departure.',
        errorTitle: 'Failed to load fixed departures',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
